import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import http from '../utils/request';
import { motion, AnimatePresence } from 'framer-motion';
import { useModalEscape } from '../utils/useModalEscape';

const StockMovements = () => {
  const navigate = useNavigate();
  const [movements, setMovements] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [filterProduct, setFilterProduct] = useState('');
  const [filterType, setFilterType] = useState('');
  const [productSearch, setProductSearch] = useState('');
  const [showProductSuggestions, setShowProductSuggestions] = useState(false);

  const [formData, setFormData] = useState({
    productId: '',
    type: 'adjustment',
    quantity: '',
    reason: '',
    notes: '',
  });

  // ESC key handler for modal
  useModalEscape(showModal, () => setShowModal(false));

  const typeLabels = {
    adjustment: '📝 Ajuste',
    damage: '💔 Merma/Rotura',
    loss: '❌ Pérdida',
    found: '✅ Encontrado',
    correction: '🔧 Corrección',
    return: '↩️ Devolución',
    other: '📦 Otro',
  };

  useEffect(() => {
    fetchMovements();
    fetchProducts();
  }, []);

  const fetchMovements = async () => {
    try {
      setLoading(true);
      const res = await http.get('/stock-movements');
      setMovements(res.data || []);
    } catch (err) {
      console.error('Error al cargar movimientos:', err);
      toast.error('❌ Error al cargar movimientos');
    } finally {
      setLoading(false);
    }
  };

  const fetchProducts = async () => {
    try {
      const res = await http.get('/product/all-products');
      setProducts(res.data || []);
    } catch (err) {
      console.error('Error al cargar productos:', err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.productId || !formData.type || !formData.quantity || !formData.reason) {
      toast.warn('⚠️ Completá todos los campos requeridos');
      return;
    }

    try {
      await http.post('/stock-movements', formData);
      toast.success('✅ Movimiento registrado correctamente');
      setShowModal(false);
      setFormData({
        productId: '',
        type: 'adjustment',
        quantity: '',
        reason: '',
        notes: '',
      });
      setProductSearch('');
      setShowProductSuggestions(false);
      fetchMovements();
      fetchProducts(); // Actualizar stocks
    } catch (err) {
      console.error('Error al crear movimiento:', err);
      toast.error(err.response?.data?.message || '❌ Error al crear movimiento');
    }
  };

  const handleProductSearch = (value) => {
    setProductSearch(value);
    setShowProductSuggestions(value.length >= 2);
  };

  const selectProduct = (product) => {
    setFormData({ ...formData, productId: product._id });
    setProductSearch(`${product.name} (${product.barcode}) - Stock: ${product.stock}`);
    setShowProductSuggestions(false);
  };

  const filteredProductsForModal = products.filter(p => {
    if (productSearch.length < 2) return true;
    const lower = productSearch.toLowerCase();
    return p.name.toLowerCase().includes(lower) || 
           (p.barcode && p.barcode.toLowerCase().includes(lower));
  });

  const handleDelete = async (id) => {
    if (!window.confirm('⚠️ ¿Eliminar este movimiento? Se revertirá el ajuste de stock.')) {
      return;
    }

    try {
      await http.delete(`/stock-movements/${id}`);
      toast.success('✅ Movimiento eliminado y stock revertido');
      fetchMovements();
      fetchProducts();
    } catch (err) {
      console.error('Error al eliminar movimiento:', err);
      toast.error(err.response?.data?.message || '❌ Error al eliminar movimiento');
    }
  };

  const _getProductName = (productId) => {
    const product = products.find(p => p._id === productId);
    return product ? `${product.name} (${product.barcode})` : 'Producto desconocido';
  };

  const filteredMovements = movements.filter(m => {
    if (filterProduct && m.productId?._id !== filterProduct) return false;
    if (filterType && m.type !== filterType) return false;
    return true;
  });

  // Estilos
  const btnNav = {
    padding: '10px 20px',
    borderRadius: '8px',
    border: 'none',
    cursor: 'pointer',
    fontWeight: 'bold',
    fontSize: '14px',
    background: '#6c757d',
    color: 'white',
    transition: 'all 0.3s',
  };

  const btnGreen = {
    ...btnNav,
    background: '#28a745',
  };

  const btnRed = {
    ...btnNav,
    background: '#dc3545',
  };

  const inputStyle = {
    padding: '8px',
    borderRadius: '6px',
    border: '1px solid #ccc',
    fontSize: '14px',
    width: '100%',
  };

  const modalBackdrop = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  };

  const modalContent = {
    backgroundColor: 'white',
    padding: '30px',
    borderRadius: '12px',
    maxWidth: '600px',
    width: '90%',
    maxHeight: '90vh',
    overflowY: 'auto',
  };

  return (
    <div style={{ padding: '30px', fontFamily: 'Arial, sans-serif' }}>
      <div style={{ marginBottom: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
        <h1>📊 Movimientos de Inventario</h1>
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
          <button style={btnNav} onClick={() => navigate('/')}>🏠 Dashboard</button>
          <button style={btnNav} onClick={() => navigate('/products')}>📦 Productos</button>
          <button style={btnGreen} onClick={() => setShowModal(true)}>➕ Nuevo Movimiento</button>
        </div>
      </div>

      {/* Filtros */}
      <div style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
        <h3 style={{ marginBottom: '15px', fontSize: '16px' }}>🔍 Filtros</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '15px' }}>
          <div>
            <label style={{ fontSize: '14px', fontWeight: 'bold', display: 'block', marginBottom: '5px' }}>Producto:</label>
            <select value={filterProduct} onChange={(e) => setFilterProduct(e.target.value)} style={inputStyle}>
              <option value="">— Todos —</option>
              {products.map(p => (
                <option key={p._id} value={p._id}>{p.name} ({p.barcode})</option>
              ))}
            </select>
          </div>
          <div>
            <label style={{ fontSize: '14px', fontWeight: 'bold', display: 'block', marginBottom: '5px' }}>Tipo:</label>
            <select value={filterType} onChange={(e) => setFilterType(e.target.value)} style={inputStyle}>
              <option value="">— Todos —</option>
              {Object.entries(typeLabels).map(([key, label]) => (
                <option key={key} value={key}>{label}</option>
              ))}
            </select>
          </div>
        </div>
        <button
          style={{ ...btnNav, marginTop: '10px', background: '#6c757d' }}
          onClick={() => { setFilterProduct(''); setFilterType(''); }}
        >
          🗑️ Limpiar filtros
        </button>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <p>Cargando movimientos...</p>
        </div>
      ) : filteredMovements.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <p>No hay movimientos registrados.</p>
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', backgroundColor: 'white' }}>
            <thead>
              <tr style={{ backgroundColor: '#007bff', color: 'white' }}>
                <th style={{ padding: '12px', textAlign: 'left' }}>Fecha</th>
                <th style={{ padding: '12px', textAlign: 'left' }}>Producto</th>
                <th style={{ padding: '12px', textAlign: 'left' }}>Tipo</th>
                <th style={{ padding: '12px', textAlign: 'center' }}>Cantidad</th>
                <th style={{ padding: '12px', textAlign: 'center' }}>Stock Anterior</th>
                <th style={{ padding: '12px', textAlign: 'center' }}>Stock Nuevo</th>
                <th style={{ padding: '12px', textAlign: 'left' }}>Motivo</th>
                <th style={{ padding: '12px', textAlign: 'center' }}>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {filteredMovements.map((movement) => (
                <tr key={movement._id} style={{ borderBottom: '1px solid #dee2e6' }}>
                  <td style={{ padding: '12px' }}>
                    {new Date(movement.createdAt).toLocaleString('es-AR', {
                      day: '2-digit',
                      month: '2-digit',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </td>
                  <td style={{ padding: '12px' }}>
                    {movement.productId?.name || 'Desconocido'}
                    <br />
                    <small style={{ color: '#666' }}>{movement.productId?.barcode}</small>
                  </td>
                  <td style={{ padding: '12px' }}>{typeLabels[movement.type] || movement.type}</td>
                  <td style={{ padding: '12px', textAlign: 'center', fontWeight: 'bold', color: movement.quantity >= 0 ? '#28a745' : '#dc3545' }}>
                    {movement.quantity >= 0 ? '+' : ''}{movement.quantity}
                  </td>
                  <td style={{ padding: '12px', textAlign: 'center' }}>{movement.previousStock}</td>
                  <td style={{ padding: '12px', textAlign: 'center', fontWeight: 'bold' }}>{movement.newStock}</td>
                  <td style={{ padding: '12px' }}>
                    {movement.reason}
                    {movement.notes && (
                      <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                        Notas: {movement.notes}
                      </div>
                    )}
                  </td>
                  <td style={{ padding: '12px', textAlign: 'center' }}>
                    <button
                      style={{ ...btnRed, padding: '6px 12px', fontSize: '12px' }}
                      onClick={() => handleDelete(movement._id)}
                    >
                      🗑️ Eliminar
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal nuevo movimiento */}
      <AnimatePresence>
        {showModal && (
          <motion.div
            style={modalBackdrop}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ type: 'spring', stiffness: 260, damping: 20 }}
              style={modalContent}
              onClick={(e) => e.stopPropagation()}
            >
              <h2 style={{ textAlign: 'center', marginBottom: '20px' }}>➕ Nuevo Movimiento de Stock</h2>
              <form onSubmit={handleSubmit} style={{ display: 'grid', gap: '15px' }}>
                <div style={{ position: 'relative' }}>
                  <label style={{ fontSize: '14px', fontWeight: 'bold', display: 'block', marginBottom: '5px' }}>
                    Producto *
                  </label>
                  <input
                    type="text"
                    value={productSearch}
                    onChange={(e) => handleProductSearch(e.target.value)}
                    onFocus={() => productSearch.length >= 2 && setShowProductSuggestions(true)}
                    style={inputStyle}
                    placeholder="Buscar producto por nombre o código..."
                    required={!formData.productId}
                  />
                  {!formData.productId && productSearch.length > 0 && productSearch.length < 2 && (
                    <small style={{ color: '#666', fontSize: '12px' }}>Ingresá al menos 2 caracteres para buscar</small>
                  )}
                  {showProductSuggestions && filteredProductsForModal.length > 0 && (
                    <div style={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      right: 0,
                      background: '#fff',
                      border: '1px solid #ccc',
                      borderTop: 'none',
                      borderRadius: '0 0 6px 6px',
                      maxHeight: '200px',
                      overflowY: 'auto',
                      zIndex: 1000,
                      boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                      marginTop: '2px'
                    }}>
                      {filteredProductsForModal.slice(0, 20).map((p) => (
                        <div
                          key={p._id}
                          onClick={() => selectProduct(p)}
                          style={{
                            padding: '10px 12px',
                            cursor: 'pointer',
                            borderBottom: '1px solid #f0f0f0',
                            fontSize: '13px'
                          }}
                          onMouseEnter={(e) => e.target.style.background = '#f8f9fa'}
                          onMouseLeave={(e) => e.target.style.background = '#fff'}
                        >
                          <div style={{ fontWeight: 'bold' }}>{p.name}</div>
                          <div style={{ fontSize: '11px', color: '#666' }}>
                            Código: {p.barcode} · Stock actual: {p.stock}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div>
                  <label style={{ fontSize: '14px', fontWeight: 'bold', display: 'block', marginBottom: '5px' }}>
                    Tipo de movimiento *
                  </label>
                  <select
                    value={formData.type}
                    onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                    style={inputStyle}
                    required
                  >
                    {Object.entries(typeLabels).map(([key, label]) => (
                      <option key={key} value={key}>{label}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label style={{ fontSize: '14px', fontWeight: 'bold', display: 'block', marginBottom: '5px' }}>
                    Cantidad * {formData.type === 'adjustment' || formData.type === 'correction' ? '(positivo para sumar, negativo para restar)' : ''}
                  </label>
                  <input
                    type="number"
                    value={formData.quantity}
                    onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
                    style={inputStyle}
                    required
                    step="1"
                  />
                </div>

                <div>
                  <label style={{ fontSize: '14px', fontWeight: 'bold', display: 'block', marginBottom: '5px' }}>
                    Motivo *
                  </label>
                  <input
                    type="text"
                    value={formData.reason}
                    onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                    style={inputStyle}
                    required
                    placeholder="Ej: Producto vencido, rotura en transporte..."
                    maxLength="500"
                  />
                </div>

                <div>
                  <label style={{ fontSize: '14px', fontWeight: 'bold', display: 'block', marginBottom: '5px' }}>
                    Notas adicionales
                  </label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    style={{ ...inputStyle, minHeight: '80px' }}
                    placeholder="Información adicional opcional..."
                    maxLength="1000"
                  />
                </div>

                <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px', marginTop: '10px' }}>
                  <button type="submit" style={btnGreen}>Guardar</button>
                  <button type="button" onClick={() => setShowModal(false)} style={btnRed}>Cancelar</button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default StockMovements;
